// Wall traversal engine. Walls (INTERSECTION_2 auras like Wall of
// Fire, Wall of Air, etc.) sit on the edge between two cells. Two
// behaviors compose:
//
//   Blocking. When a unit's reach computation would step across an
//   edge that has a blocking wall, the destination is filtered out.
//
//   Damage / trigger. When a unit actually crosses an edge with an
//   on_edge_traverse-listening wall, those entries fire with the
//   crossing unit as `watchTarget`.
//
// All inputs are read-only; mutation lives in the caller. The
// per-wall blocking predicate is parsed from the wall's automation
// (BLOCK_EDGE_TRAVERSAL static entries) so adding a new wall type is
// purely a JSON change. No engine code.

import { hasAirborne } from './movement';
import { countsAsMinion } from './typeline';
import { isBurrowed, isSubmerged } from './burrowSubmerge';
import { AURA_SHAPE } from './auraPlacement';
import { OP } from '../automation/schema';

/**
 * Return the canonical edge of `from` that's crossed to reach `to`,
 * or null if the two cells aren't orthogonally adjacent (no edge to
 * cross). Diagonal moves return null. Walls only intercept cardinal
 * steps; an Airborne flyer cutting a corner has no edge to interact
 * with.
 *
 * Edges are named from `from`'s perspective: a step north reads as
 * "crossing `from`'s top edge."
 */
export function crossingEdge(from, to) {
  if (!from || !to) return null;
  const dc = to.col - from.col;
  const dr = to.row - from.row;
  if (Math.abs(dc) + Math.abs(dr) !== 1) return null;
  if (dr === -1) return 'top';
  if (dr ===  1) return 'bottom';
  if (dc === -1) return 'left';
  if (dc ===  1) return 'right';
  return null;
}

/**
 * The opposite-edge canonical name. The same physical edge between
 * cells A and B reads as A's 'right' and B's 'left'; an aura placed
 * there carries ONE of those keyings in `_auraAnchor.edge`. To match
 * walls on an edge, callers should check both keyings.
 */
function flipEdge(edge) {
  if (edge === 'top')    return 'bottom';
  if (edge === 'bottom') return 'top';
  if (edge === 'left')   return 'right';
  if (edge === 'right')  return 'left';
  return null;
}

/**
 * Find every INTERSECTION_2 aura (wall) sitting on the edge between
 * `from` and `to`. Returns the live card instances so callers can read
 * their automation for predicates / triggers.
 */
export function findWallsBetween(from, to, meshes) {
  const edge = crossingEdge(from, to);
  if (!edge) return [];
  const flip = flipEdge(edge);
  const out = [];
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || card.type !== 'Aura') continue;
    if (card._auraShape?.shape !== AURA_SHAPE.INTERSECTION_2) continue;
    const a = card._auraAnchor;
    if (!a) continue;
    // The aura's anchor either matches `from` + `edge`, or `to` + `flip`.
    const matchFromSide = a.col === from.col && a.row === from.row && a.edge === edge;
    const matchToSide   = a.col === to.col   && a.row === to.row   && a.edge === flip;
    if (matchFromSide || matchToSide) out.push(card);
  }
  return out;
}

// ── Predicate evaluator (BLOCK_EDGE_TRAVERSAL `if`) ─────────────────
//
// Small predicate language consulted by the wall blocker. Lives here
// rather than in conditions.js because the predicate operates on the
// unit attempting to cross. Distinct from `watch_target_filter`'s
// after-the-fact context, and from static entry conditions which
// gate aura sources.

/**
 * Evaluate a wall's blocking predicate against a unit. Predicates are
 * declarative JSON read from BLOCK_EDGE_TRAVERSAL entries' `if` field.
 *
 * Supported predicates:
 *   { type: 'any_of', conditions: [...] }
 *   { type: 'all_of', conditions: [...] }
 *   { type: 'is_minion' }. The crosser is a minion (Wall of
 *                                       Air restricts "Minions with…";
 *                                       Avatars cross freely)
 *   { type: 'airborne' }. Unit has Airborne keyword
 *   { type: 'on_ground' }. Not airborne, not subsurface
 *   { type: 'power_at_most', amount }. Effective power ≤ amount
 *   { type: 'power_at_least', amount }. Effective power ≥ amount
 */
export function unitMatchesEdgePredicate(unit, predicate) {
  if (!predicate) return false;
  if (predicate.type === 'any_of') {
    return (predicate.conditions || []).some((c) => unitMatchesEdgePredicate(unit, c));
  }
  if (predicate.type === 'all_of') {
    return (predicate.conditions || []).every((c) => unitMatchesEdgePredicate(unit, c));
  }
  if (predicate.type === 'is_minion') return countsAsMinion(unit);
  if (predicate.type === 'airborne')  return !!hasAirborne(unit);
  if (predicate.type === 'on_ground') return !hasAirborne(unit) && !isBurrowed(unit) && !isSubmerged(unit);
  const effectivePower = (unit?.currentAttack ?? unit?.baseAttack ?? 0) + (unit?._auraStatDelta || 0);
  if (predicate.type === 'power_at_most')  return effectivePower <= (predicate.amount ?? Infinity);
  if (predicate.type === 'power_at_least') return effectivePower >= (predicate.amount ?? 0);
  return false;
}

/**
 * Read every BLOCK_EDGE_TRAVERSAL entry from `fullWall` and return
 * true if any predicate matches `unit`.
 *
 * Each block entry looks like:
 *   { trigger: 'static', effect: [{ op: 'block_edge_traversal', args: { if: { ... } } }] }
 *
 * Multiple block entries combine with OR. If any blocks, the unit
 * can't cross. An entry with no `if` (or a missing `if`) blocks
 * universally (no observed card today; reserved for "Wall blocks
 * everything").
 */
export function wallBlocksUnit(fullWall, unit) {
  if (!fullWall || !unit) return false;
  const entries = (fullWall.automation || []).filter((e) => e.trigger === 'static');
  for (const e of entries) {
    for (const eff of (e.effect || [])) {
      if (eff.op !== OP.BLOCK_EDGE_TRAVERSAL) continue;
      const pred = eff.args?.if;
      if (!pred || unitMatchesEdgePredicate(unit, pred)) return true;
    }
  }
  return false;
}

/**
 * High-level integration helper used by the movement-reach engine.
 * Returns true if the step from `fromCell` to `toCell` is blocked by
 * any wall on the crossed edge that targets `unit`.
 *
 * @param {object} unit
 * @param {{col,row}} fromCell
 * @param {{col,row}} toCell
 * @param {Iterable<[any, { userData, position }]>} meshes
 * @param {(cardId: string) => object | null} getFullCard
 */
export function isEdgeBlockedForUnit(unit, fromCell, toCell, meshes, getFullCard) {
  const walls = findWallsBetween(fromCell, toCell, meshes);
  if (walls.length === 0) return false;
  for (const w of walls) {
    const full = getFullCard(w.cardId);
    if (wallBlocksUnit(full, unit)) return true;
  }
  return false;
}
